import { Modal, ModalProps } from '@chakra-ui/react';
import { datadogLogs } from '@datadog/browser-logs';
import { logContext } from '@shared/services';
import {
  SESSION_REMINDERS,
  SessionReminderId,
} from '@web-app/constants/session-reminders';
import React, { createContext, useContext, useEffect, useRef } from 'react';

export interface SessionReminderContextValue {
  roomId: string;
  appointmentId?: string;
  providerClerkId?: string;
  providerType: 'mnt' | 'prescriber' | 'unknown';
}

export const SessionReminderContext =
  createContext<SessionReminderContextValue | null>(null);

/** Count actual modal openings, after the caller's tag and flag gates. */
export const SessionReminderModal = ({
  popupId,
  ...props
}: ModalProps & { popupId: SessionReminderId }) => {
  const context = useContext(SessionReminderContext);
  const lastOpening = useRef<string | null>(null);

  useEffect(() => {
    if (!props.isOpen) {
      lastOpening.current = null;
      return;
    }
    if (!context) return;

    const openingKey = `${context.roomId}:${popupId}`;
    if (lastOpening.current === openingKey) return;
    lastOpening.current = openingKey;

    const reminder = SESSION_REMINDERS[popupId];
    try {
      datadogLogs.logger.info(
        'Session reminder popup shown',
        logContext('session-reminders', 'popup-shown', {
          detail: 'An eligible session reminder opened for the provider',
          ...context,
          popupId,
          popupName: reminder.name,
          popupCategory: reminder.category,
          schemaVersion: 1,
        }),
      );
    } catch {
      // Telemetry must not interrupt the session or prevent dismissal.
    }
  }, [context, popupId, props.isOpen]);

  return <Modal {...props} />;
};
